import React, { useEffect, useState } from "react";
import {
  FaFilter,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUserCheck,
  FaCalendarAlt,
  FaMobile,
  FaEnvelope,
  FaTag,
  FaImage,
  FaCalendarDay,
} from "react-icons/fa";
import Swal from "../../utils/swalCompat";
import { getDepositHistory } from "../../api/endpoints";
import PageHeader from "../../components/PageHeader";

const DepositHistoryList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showFilter, setShowFilter] = useState(false);

  // ✅ API se baseUrl aayega: "http://192.168.1.27:5000"
  const [imagePath, setImagePath] = useState("");

  // ============================================================
  // IMAGE URL BUILDER
  // ============================================================
  const getImageUrl = (img) => {
    if (!img) return "";

    // Already full URL
    if (img.startsWith("http://") || img.startsWith("https://")) {
      return img;
    }

    // Base64
    if (img.startsWith("data:")) {
      return img;
    }

    // Join with imagePath (baseUrl from API)
    const basePath = (imagePath || "").replace(/\/+$/, "");
    const fileName = img.replace(/^\/+/, "");

    if (!basePath) {
      console.warn("⚠️ imagePath empty — relative path returned:", img);
      return img;
    }

    return `${basePath}/${fileName}`;
  };

  // ============================================================
  // FETCH DEPOSIT HISTORY
  // ============================================================
  const fetchDepositHistory = async () => {
    try {
      setLoading(true);

      const params = { page, limit };

      // Date filter logic
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (startDate && !endDate) {
        params.startDate = startDate;
      } else if (!startDate && endDate) {
        params.endDate = endDate;
      }

      console.log("Fetching Deposit History with params:", params);
      const response = await getDepositHistory(params);
      console.log("Deposit History Response:", response);

      // ✅ Handle response structure
      const responseData = response?.data?.data || response?.data || response;

      if (response?.success === false) {
        Swal.fire(
          "Error",
          response?.message || "Failed to fetch deposit history",
          "error",
        );
        return;
      }

      setTransactions(responseData?.transactions || []);
      setTotalPages(responseData?.totalPages || 1);
      setTotalItems(responseData?.total || 0);
      if (responseData?.page) setPage(responseData.page);

      // ✅ baseUrl API se extract karo
      const baseUrl =
        responseData?.baseUrl ||
        response?.data?.baseUrl ||
        response?.baseUrl ||
        "";

      if (baseUrl) {
        setImagePath(baseUrl);
        console.log("✅ Image Base URL set:", baseUrl);
      } else {
        console.warn("⚠️ baseUrl not found in API response");
      }
    } catch (error) {
      console.error("Error fetching deposit history:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to fetch deposit history",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepositHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, page]);

  // ============================================================
  // STATUS BADGE
  // ============================================================
  const getStatusBadge = (status) => {
    const statusMap = {
      APPROVED: {
        color: "bg-success",
        icon: <FaCheckCircle />,
        text: "Approved",
      },
      PENDING: {
        color: "bg-warning text-dark",
        icon: <FaClock />,
        text: "Pending",
      },
      REJECTED: {
        color: "bg-danger",
        icon: <FaTimesCircle />,
        text: "Rejected",
      },
    };
    const statusData = statusMap[status] || statusMap["PENDING"];
    return (
      <span
        className={`badge ${statusData.color} d-inline-flex align-items-center gap-1`}
      >
        {statusData.icon} {statusData.text}
      </span>
    );
  };

  // ============================================================
  // FORMATTERS
  // ============================================================
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ============================================================
  // VIEW SCREENSHOT — uses baseUrl from API
  // ============================================================
  const viewScreenshot = (screenshot) => {
    if (!screenshot) {
      Swal.fire("Info", "No screenshot available for this transaction", "info");
      return;
    }

    const url = getImageUrl(screenshot);
    console.log("🖼️ Opening screenshot:", url);

    Swal.fire({
      title: "Transaction Screenshot",
      imageUrl: url,
      imageWidth: 400,
      imageHeight: "auto",
      imageAlt: "Screenshot",
      confirmButtonText: "Close",
      showCloseButton: true,
    });
  };

  // ============================================================
  // PAGINATION
  // ============================================================
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const hasActiveFilters = startDate || endDate;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lead-page">
      <PageHeader
        title="Deposit Report"
        subtitle="View all deposit transactions"
      />

      <div className="card mb-4 card-light">
        {/* ===== Card Header ===== */}
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">Deposit Transactions</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <div className="input-group" style={{ width: "160px" }}>
                  <span className="input-group-text">
                    <FaCalendarAlt />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <div className="input-group" style={{ width: "160px" }}>
                  <span className="input-group-text">
                    <FaCalendarDay />
                  </span>
                  <input
                    type="date"
                    className="form-control"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? " Hide" : " Filter"}
            </button>
          </div>
        </div>

        {/* ===== Card Body ===== */}
        <div className="card-body">
          {/* Stats Bar */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {transactions.length} of {totalItems} transactions
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* Active Filters Chips */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {startDate && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  From: {formatDateDisplay(startDate)}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setStartDate("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {endDate && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  To: {formatDateDisplay(endDate)}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => {
                      setEndDate("");
                      setPage(1);
                    }}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* ===== TABLE ===== */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User Details</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>UTR Number</th>
                  <th>Status</th>
                  <th>Screenshot</th>
                  <th>Approved By</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && transactions.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length > 0 ? (
                  transactions.map((transaction, index) => {
                    // ✅ Display name fallback
                    const displayName =
                      transaction.userName?.trim() ||
                      (transaction.userMobile
                        ? `User (${transaction.userMobile})`
                        : "Unknown User");

                    const avatarChar =
                      transaction.userName?.charAt(0) ||
                      transaction.userMobile?.charAt(0) ||
                      "U";

                    const imgUrl = getImageUrl(transaction.screenshot);

                    return (
                      <tr key={transaction._id}>
                        <td>{(page - 1) * limit + index + 1}</td>

                        {/* User Details */}
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {/* <div
                              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                              style={{
                                width: "35px",
                                height: "35px",
                                fontSize: "14px",
                                fontWeight: "bold",
                                flexShrink: 0,
                              }}
                            >
                              {avatarChar.toUpperCase()}
                            </div> */}
                            <div>
                              <div className="fw-bold">{displayName}</div>
                              {transaction.userMobile && (
                                <small className="text-muted d-block">
                                  <FaMobile className="me-1" size={12} />
                                  {transaction.userMobile}
                                </small>
                              )}
                              {transaction.userEmail && (
                                <small className="text-muted d-block">
                                  <FaEnvelope className="me-1" size={12} />
                                  {transaction.userEmail}
                                </small>
                              )}
                              {transaction.referralCode && (
                                <small className="text-muted d-block">
                                  <FaTag className="me-1" size={12} />
                                  Referral: {transaction.referralCode}
                                </small>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Amount */}
                        <td>
                          <span className="fw-bold text-success">
                            {formatCurrency(transaction.amount)}
                          </span>
                        </td>

                        {/* Payment Method */}
                        <td>
                          <span
                            className={`badge ${
                              transaction.paymentMethod === "ADMIN"
                                ? "bg-secondary"
                                : "bg-info text-dark"
                            }`}
                          >
                            {transaction.paymentMethod || "N/A"}
                          </span>
                        </td>

                        {/* UTR */}
                        <td>
                          <code className="bg-light p-1 rounded">
                            {transaction.utrNumber || "N/A"}
                          </code>
                        </td>

                        {/* Status */}
                        <td>{getStatusBadge(transaction.status)}</td>

                        {/* ✅ Screenshot with thumbnail + View button */}
                        <td>
                          {imgUrl ? (
                            <div className="d-flex align-items-center gap-2">
                              <img
                                src={imgUrl}
                                alt="screenshot"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  border: "1px solid #E2E8F0",
                                  background: "#F8FAFC",
                                }}
                                onClick={() =>
                                  viewScreenshot(transaction.screenshot)
                                }
                                onError={(e) => {
                                  console.error(
                                    "❌ Screenshot failed:",
                                    e.currentTarget.src,
                                  );
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() =>
                                  viewScreenshot(transaction.screenshot)
                                }
                                title="View Screenshot"
                              >
                                <FaImage /> View
                              </button>
                            </div>
                          ) : (
                            <span className="text-muted small">No image</span>
                          )}
                        </td>

                        {/* Approved By */}
                        <td>
                          {transaction.approvedBy ? (
                            <span className="text-muted small">
                              <FaUserCheck className="me-1" />
                             Admin
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>

                        {/* Date */}
                        <td>
                          <div className="d-flex flex-column small">
                            <span>{formatDate(transaction.createdAt)}</span>
                            {transaction.approvedAt && (
                              <small className="text-muted">
                                ✓ Admin
                              </small>
                            )}
                            {transaction.remark && (
                              <small className="text-danger">
                                <FaTag className="me-1" size={11} />
                                {transaction.remark}
                              </small>
                            )}
                            <small className="text-muted">
                              Order: {transaction.orderId}
                            </small>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      {hasActiveFilters
                        ? "No transactions found matching your filters"
                        : "No Deposit Transactions Found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ===== Pagination ===== */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${
                      pageNum === page ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: "40px" }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {transactions.length} of {totalItems} transactions | Page{" "}
              {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositHistoryList;
